import { getAuth, type User } from 'firebase/auth'

import { getLittaPublicApiBaseUrl } from '@/lib/api/litta-api-base-url'
import { LittaApiError, littaApiErrorMessage } from '@/lib/api/litta-api-error'

export {
    hasApiErrorCode,
    isForbiddenError,
    littaApiErrorMessage,
    LittaApiError,
    retryUnlessClientError,
} from '@/lib/api/litta-api-error'

export function waitForAuthUser(): Promise<User | null> {
    const auth = getAuth()
    if (auth.currentUser) {
        return Promise.resolve(auth.currentUser)
    }

    return new Promise(resolve => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            unsubscribe()
            resolve(user)
        })
    })
}

export async function getFirebaseAuthIdToken(): Promise<string> {
    const user = await waitForAuthUser()
    if (!user) {
        throw new LittaApiError('Not signed in', 401)
    }

    const token = await user.getIdToken()
    if (!token) {
        throw new LittaApiError('Not signed in', 401)
    }

    return token
}

async function getFirebaseIdToken(): Promise<string> {
    return getFirebaseAuthIdToken()
}

export async function littaApiFetch(
    input: string,
    init?: RequestInit,
): Promise<Response> {
    const token = await getFirebaseIdToken()

    const isFormData = init?.body instanceof FormData

    return fetch(input, {
        ...init,
        headers: {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            Authorization: `Bearer ${token}`,
            ...(init?.headers ?? {}),
        },
    })
}

export async function parseError(
    res: Response,
): Promise<{ message: string; code?: string }> {
    try {
        const body = (await res.json()) as {
            message?: string
            error?: string
            code?: string
        }
        return {
            message: body.message ?? body.error ?? res.statusText,
            code: body.code,
        }
    } catch {
        const text = await res.text().catch(() => '')
        return { message: text || res.statusText }
    }
}

export async function littaApiFetchJson<T>(
    path: string,
    init?: RequestInit,
): Promise<T> {
    const res = await littaApiFetch(
        `${getLittaPublicApiBaseUrl()}${path}`,
        init,
    )

    if (!res.ok) {
        const { message, code } = await parseError(res)
        throw new LittaApiError(
            littaApiErrorMessage(res.status, message),
            res.status,
            code,
            message,
        )
    }

    return res.json() as Promise<T>
}
